import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  assignBookingRoomGuest,
  fetchBookingJourney,
  JOURNEY_API_STORAGE_KEY,
  JOURNEY_TOKEN_STORAGE_KEY,
  setCurrentJourneyToken,
  updateBookingJourneyLanguage,
} from "../services/reservationService";
import { clearGuestData } from "../utils/storage";
import {
  setBookingOwnerContext,
  storeJourneyAccess,
} from "../utils/journeyAccess";
import { setStoredAppLanguage } from "../hooks/useAppLanguage";

const bookingTranslations = {
  de: {
    kicker: "Buchung",
    title: "Zimmer zuweisen",
    loading: "Buchung wird geladen...",
    unavailable: "Diese Buchung ist gerade nicht verfügbar.",
    checkinComplete: "Online Check-in ist abgeschlossen.",
    intro: "Bitte geben Sie für jedes Zimmer an, wer es nutzt.",
    room: "Zimmer",
    roomFallback: "Zimmer",
    stay: "Aufenthalt",
    bookerMatchQuestion: "Ist das Ihr Zimmer?",
    bookerIsGuest: "Ja, das ist mein Zimmer",
    differentGuest: "Nein, ein anderer Gast",
    knownRoomGuest: "Bekannter Zimmergast",
    firstName: "Vorname",
    lastName: "Nachname",
    email: "E-Mail",
    enterEmail: "Gast-E-Mail eingeben",
    language: "Sprache",
    languageDe: "Deutsch",
    languageEn: "Englisch",
    save: "Zimmergast speichern",
    saving: "Speichern...",
    assignmentComplete: "Danke. Wir haben die Zimmerdaten gespeichert.",
    guestLinkSent: "Der Gast erhält seinen eigenen Link für dieses Zimmer.",
    guestLinkScheduled: "Der Gast-Link ist geplant und wird automatisch vor der Anreise versendet.",
    roomAppUnavailable: "Nach dem Online Check-in ist die Zimmer-App verfügbar.",
    openRoomApp: "Zimmer-App öffnen",
    startCheckin: "Online Check-in starten",
    continueRoomApp: "Zur Zimmer-App",
    missingOwnerToken: "Für dieses Zimmer fehlt der Online-Check-in-Link.",
    required: "Bitte füllen Sie die fehlenden Gastdaten aus.",
    refreshFailed: "Die Zimmerzuweisung wurde gespeichert, aber die Buchung konnte nicht aktualisiert werden.",
  },
  en: {
    kicker: "Booking",
    title: "Assign rooms",
    loading: "Loading booking...",
    unavailable: "This booking is not available right now.",
    checkinComplete: "Online check-in is complete.",
    intro: "Please specify who will use each room.",
    room: "Room",
    roomFallback: "Room",
    stay: "Stay",
    bookerMatchQuestion: "Is this your room?",
    bookerIsGuest: "Yes, this is my room",
    differentGuest: "No, another guest",
    knownRoomGuest: "Known room guest",
    firstName: "First name",
    lastName: "Last name",
    email: "Email",
    enterEmail: "Enter guest email",
    language: "Language",
    languageDe: "German",
    languageEn: "English",
    save: "Save room guest",
    saving: "Saving...",
    assignmentComplete: "Thank you. The room details have been saved.",
    guestLinkSent: "The guest will receive their own link for this room.",
    guestLinkScheduled: "The guest link is scheduled and will be sent automatically before arrival.",
    roomAppUnavailable: "The room app will be available after online check-in.",
    openRoomApp: "Open room app",
    startCheckin: "Start online check-in",
    continueRoomApp: "Continue to room app",
    missingOwnerToken: "This room is missing its online check-in link.",
    required: "Please fill the missing guest details.",
    refreshFailed: "The room assignment was saved, but the booking could not be refreshed.",
  },
};

const normalizeBooker = (booker = {}) => ({
  first_name: booker.firstName || booker.first_name || "",
  last_name: booker.lastName || booker.last_name || "",
  email: booker.email || "",
});

const tokenFromJourneyReference = (value) => {
  const url = String(value || "").trim();
  if (!url) return "";

  try {
    const parsed = new URL(url, window.location.origin);
    const match = parsed.pathname.match(/\/j\/([^/]+)/) || parsed.pathname.match(/\/journey\/([^/]+)/);
    return match ? decodeURIComponent(match[1]) : "";
  } catch {
    const match = url.match(/\/j\/([^/]+)/) || url.match(/\/journey\/([^/]+)/);
    return match ? decodeURIComponent(match[1]) : "";
  }
};

const appPathFromUrl = (value, fallback = "") => {
  const url = String(value || "").trim();
  if (!url) return fallback;
  if (url.startsWith("/")) return url;

  try {
    const parsed = new URL(url, window.location.origin);
    return `${parsed.pathname}${parsed.search}${parsed.hash}` || fallback;
  } catch {
    return fallback;
  }
};

const normalizeBookingLanguage = (value) => (value === "en" ? "en" : "de");

const languageLabel = (language, t) => {
  if (language === "en") return t.languageEn;
  if (language === "de") return t.languageDe;
  return String(language || "").toUpperCase();
};

const reservationIdOf = (reservation = {}) => reservation.reservationId || reservation.reservation_id || "";

const normalizeReservationGuest = (guest = {}) => ({
  firstName: String(guest.firstName || guest.first_name || "").trim(),
  lastName: String(guest.lastName || guest.last_name || "").trim(),
  email: String(guest.email || "").trim(),
});

const knownGuestName = (reservation = {}) => {
  const guest = normalizeReservationGuest(reservation.guest);
  return [guest.firstName, guest.lastName].filter(Boolean).join(" ");
};

const normalizedNamePart = (value) => String(value || "")
  .trim()
  .replace(/\s+/g, " ")
  .toLocaleLowerCase();

const guestMatchesBooker = (reservation = {}, booker = {}) => {
  const guest = normalizeReservationGuest(reservation.guest);
  const guestFirstName = normalizedNamePart(guest.firstName);
  const guestLastName = normalizedNamePart(guest.lastName);
  const bookerFirstName = normalizedNamePart(booker.first_name || booker.firstName);
  const bookerLastName = normalizedNamePart(booker.last_name || booker.lastName);

  return Boolean(
    guestFirstName &&
    guestLastName &&
    bookerFirstName &&
    bookerLastName &&
    guestFirstName === bookerFirstName &&
    guestLastName === bookerLastName
  );
};

const formatStay = (reservation, language) => {
  const values = [reservation.arrival, reservation.departure]
    .filter(Boolean)
    .map((value) => {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "";
      return new Intl.DateTimeFormat(language === "en" ? "en-GB" : "de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }).format(date);
    })
    .filter(Boolean);

  return values.join(" - ");
};

const initialAssignment = {
  mode: "",
  firstName: "",
  lastName: "",
  email: "",
  saving: false,
  saved: false,
  emailPending: false,
  error: "",
};

const createAssignments = (reservations = [], current = {}) => {
  const next = {};
  reservations.forEach((reservation) => {
    const id = reservationIdOf(reservation);
    if (!id) return;
    const guest = normalizeReservationGuest(reservation.guest);
    const previous = current[id] || {};
    next[id] = {
      ...initialAssignment,
      ...previous,
      mode: reservation.isBookerGuest ? "booker" : previous.mode || "",
      firstName: previous.firstName || guest.firstName,
      lastName: previous.lastName || guest.lastName,
      email: previous.email || guest.email,
      saving: false,
      saved: reservation.guestAssignmentComplete === true || previous.saved === true,
      error: "",
    };
  });
  return next;
};

const roomLabel = (reservation, index, t) =>
  reservation.room?.name || reservation.room?.unitName || `${t.roomFallback} ${index + 1}`;

const assignmentDone = (reservation, assignment) =>
  reservation.guestAssignmentComplete === true || assignment?.saved === true;

const BookingOwnerPage = () => {
  const { bookingAccessToken } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [assignments, setAssignments] = useState({});
  const [loading, setLoading] = useState(true);
  const [languageUpdating, setLanguageUpdating] = useState(false);
  const [activeEmailReservationId, setActiveEmailReservationId] = useState("");
  const [error, setError] = useState("");
  const activeLanguage = normalizeBookingLanguage(booking?.language);
  const t = bookingTranslations[activeLanguage];
  const checkinCompleted = new URLSearchParams(location.search).get("checkin") === "complete";
  const booker = useMemo(() => normalizeBooker(booking?.booker), [booking]);
  const reservations = booking?.reservations || [];
  const selectedBookerReservationId = reservations
    .map((reservation) => reservationIdOf(reservation))
    .find((reservationId) => assignments[reservationId]?.mode === "booker")
    || reservationIdOf(reservations.find((reservation) => reservation.isBookerGuest === true) || {});
  const matchingBookerReservationId = selectedBookerReservationId
    ? ""
    : reservationIdOf(reservations.find((reservation) => {
      const id = reservationIdOf(reservation);
      const current = assignments[id] || initialAssignment;
      return !assignmentDone(reservation, current) && guestMatchesBooker(reservation, booker);
    }) || {});

  const effectiveModeFor = (reservation) => {
    const id = reservationIdOf(reservation);
    const current = assignments[id] || initialAssignment;

    if (reservation.isBookerGuest === true) return "booker";
    if (selectedBookerReservationId && id !== selectedBookerReservationId) return "guest";
    if (current.mode) return current.mode;
    return id === matchingBookerReservationId ? "" : "guest";
  };

  const allAssignmentsComplete = reservations.length > 0
    && reservations.every((reservation) => {
    const id = reservationIdOf(reservation);
    const current = assignments[id] || initialAssignment;
    return assignmentDone(reservation, current);
  });

  const canContinueToOwnerRoom = reservations.some((reservation) =>
    reservation.roomAppAvailable === true && reservation.isBookerGuest === true && reservation.ownerJourneyUrl
  );
  const showOverallAssignmentComplete = allAssignmentsComplete && booking?.bookerHasRoomAccess !== true;

  useEffect(() => {
    let isMounted = true;

    setLoading(true);
    setError("");
    setActiveEmailReservationId("");

    fetchBookingJourney(bookingAccessToken)
      .then((data) => {
        if (!isMounted) return;
        setBooking(data);
        setStoredAppLanguage(normalizeBookingLanguage(data?.language));
        setAssignments((current) => createAssignments(data.reservations || [], current));
      })
      .catch((err) => {
        if (!isMounted) return;
        setError(err.response?.data?.detail || err.response?.data?.error || bookingTranslations.de.unavailable);
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [bookingAccessToken]);

  const updateAssignment = (reservationId, patch) => {
    setAssignments((current) => ({
      ...current,
      [reservationId]: {
        ...(current[reservationId] || initialAssignment),
        ...patch,
        error: patch.error !== undefined ? patch.error : "",
        saved: patch.saved !== undefined ? patch.saved : false,
      },
    }));
  };

  const chooseBookerRoom = (reservationId) => {
    setAssignments((current) => {
      const next = { ...current };
      reservations.forEach((reservation) => {
        const id = reservationIdOf(reservation);
        next[id] = {
          ...(next[id] || initialAssignment),
          mode: id === reservationId ? "booker" : "guest",
          error: "",
          saved: false,
          emailPending: false,
        };
      });
      return next;
    });
  };

  const submitAssignment = async (reservation, modeOverride = "") => {
    const reservationId = reservationIdOf(reservation);
    const current = assignments[reservationId] || initialAssignment;
    const mode = modeOverride || effectiveModeFor(reservation);
    const isBooker = mode === "booker";
    const hasKnownName = Boolean(knownGuestName(reservation));
    const payload = isBooker
      ? { bookerIsGuest: true }
      : hasKnownName
        ? { email: current.email.trim() }
        : {
          firstName: current.firstName.trim(),
          lastName: current.lastName.trim(),
          email: current.email.trim(),
        };

    if (!isBooker && (
      !payload.email ||
      (!hasKnownName && (!payload.firstName || !payload.lastName))
    )) {
      updateAssignment(reservationId, { error: t.required });
      return;
    }

    updateAssignment(reservationId, { mode, saving: true });

    try {
      const result = await assignBookingRoomGuest(
        bookingAccessToken,
        reservationId,
        payload,
        reservation.api?.assignRoomGuest || ""
      );
      updateAssignment(reservationId, {
        mode,
        saving: false,
        saved: true,
        emailPending: result?.emailPending === true || result?.sent === false,
      });
      setActiveEmailReservationId((activeId) => activeId === reservationId ? "" : activeId);
      try {
        const refreshedBooking = await fetchBookingJourney(bookingAccessToken);
        setBooking(refreshedBooking);
        setStoredAppLanguage(normalizeBookingLanguage(refreshedBooking?.language));
        setAssignments((assignmentState) => createAssignments(
          refreshedBooking.reservations || [],
          assignmentState
        ));
      } catch {
        updateAssignment(reservationId, {
          mode,
          saving: false,
          saved: true,
          emailPending: result?.emailPending === true || result?.sent === false,
          error: t.refreshFailed,
        });
      }
    } catch (err) {
      updateAssignment(reservationId, {
        mode,
        saving: false,
        error: err.response?.data?.detail || err.response?.data?.error || t.unavailable,
      });
    }
  };

  const changeLanguage = async (nextLanguage) => {
    if (!nextLanguage || nextLanguage === activeLanguage || languageUpdating) return;

    setLanguageUpdating(true);
    setError("");

    try {
      const data = await updateBookingJourneyLanguage(
        bookingAccessToken,
        nextLanguage,
        booking?.api?.setLanguage || ""
      );
      setBooking(data);
      setStoredAppLanguage(normalizeBookingLanguage(data?.language));
      setAssignments((current) => createAssignments(data.reservations || [], current));
    } catch (err) {
      setError(err.response?.data?.detail || err.response?.data?.error || t.unavailable);
    } finally {
      setLanguageUpdating(false);
    }
  };

  const startOwnerCheckin = (reservation) => {
    const ownerToken = tokenFromJourneyReference(reservation.api?.ownerCheckIn);
    if (!ownerToken) {
      updateAssignment(reservationIdOf(reservation), { error: t.missingOwnerToken });
      return;
    }

    clearGuestData();
    setCurrentJourneyToken(ownerToken);
    localStorage.setItem(JOURNEY_TOKEN_STORAGE_KEY, ownerToken);
    localStorage.setItem("reservation-code", reservationIdOf(reservation));
    localStorage.setItem("apaleo-property-id", reservation.room?.propertyCode || "");
    localStorage.setItem("hotel-name", reservation.room?.propertyCode || "");
    localStorage.setItem("arrival", reservation.arrival || "");
    localStorage.setItem("departure", reservation.departure || "");
    localStorage.setItem("booker", JSON.stringify(booker));
    localStorage.setItem("guest-firstname", booker.first_name || "");
    localStorage.setItem("guest-lastname", booker.last_name || "");
    localStorage.setItem("email", booker.email || "");
    localStorage.setItem("rooms", JSON.stringify([{
      name: reservation.room?.name || "",
      pms_id: reservation.room?.pmsId || "",
      arrival: reservation.arrival || "",
      departure: reservation.departure || "",
      guests: [booker],
    }]));
    localStorage.setItem(JOURNEY_API_STORAGE_KEY, JSON.stringify({
      check_in: reservation.api?.ownerCheckIn || "",
      check_out: reservation.api?.ownerCheckOut || "",
    }));
    setStoredAppLanguage(activeLanguage);
    storeJourneyAccess({
      access_scope: "BOOKING_OWNER",
      capabilities: {
        onlineCheckin: true,
        checkout: true,
        payment: true,
        invoice: false,
        doorAccess: true,
        directory: true,
        support: true,
      },
    });
    setBookingOwnerContext({
      bookingAccessToken,
      bookingId: booking?.bookingId || "",
      bookerHasRoomAccess: true,
      ownerJourneyUrl: reservation.ownerJourneyUrl || "",
      reservationId: reservationIdOf(reservation),
    });
    window.dispatchEvent(new Event("booking-update"));
    navigate("/self-checkin");
  };

  const continueToOwnerRoom = () => {
    const reservation = reservations.find((item) =>
      item.roomAppAvailable === true && item.isBookerGuest === true && item.ownerJourneyUrl
    );
    if (!reservation) return;
    navigate(appPathFromUrl(reservation.ownerJourneyUrl, "/home"));
  };

  if (loading) {
    return (
      <main className="booking-owner-app">
        <section className="booking-owner-section">
          <p>{t.loading}</p>
        </section>
      </main>
    );
  }

  if (error) {
    return (
      <main className="booking-owner-app">
        <section className="booking-owner-section">
          <p className="booking-owner-kicker">{t.kicker}</p>
          <h1>{t.title}</h1>
          <p className="booking-owner-error">{error}</p>
        </section>
      </main>
    );
  }

  return (
    <main className="booking-owner-app">
      <section className="booking-owner-section">
        <p className="booking-owner-kicker">{t.kicker}</p>
        {(booking?.supportedLanguages || []).length > 1 && (
          <label className="booking-owner-language">
            <span>{t.language}</span>
            <select
              aria-label={t.language}
              disabled={languageUpdating}
              onChange={(event) => changeLanguage(event.target.value)}
              value={activeLanguage}
            >
              {(booking.supportedLanguages || []).map((supportedLanguage) => (
                <option key={supportedLanguage} value={supportedLanguage}>
                  {languageLabel(supportedLanguage, t)}
                </option>
              ))}
            </select>
          </label>
        )}
        <h1>{t.title}</h1>
        <p className="booking-owner-copy">{t.intro}</p>
        {checkinCompleted && (
          <p className="booking-owner-success">{t.checkinComplete}</p>
        )}
        {showOverallAssignmentComplete && !checkinCompleted && (
          <p className="booking-owner-success">{t.assignmentComplete}</p>
        )}
        {booking?.bookerHasRoomAccess === true && canContinueToOwnerRoom && (
          <button className="booking-owner-button secondary" type="button" onClick={continueToOwnerRoom}>
            {t.continueRoomApp}
          </button>
        )}

        <div className="booking-owner-list">
          {reservations.map((reservation, index) => {
            const reservationId = reservationIdOf(reservation);
            const current = assignments[reservationId] || initialAssignment;
            const mode = effectiveModeFor(reservation);
            const isBooker = mode === "booker";
            const isDifferentGuest = mode === "guest";
            const isAssigned = assignmentDone(reservation, current);
            const bookerAssignmentDone = reservation.isBookerGuest === true || isAssigned;
            const showGuestFields = isDifferentGuest && !isAssigned;
            const showBookerSave = isBooker && !bookerAssignmentDone;
            const showStartCheckin = isBooker && bookerAssignmentDone;
            const guest = normalizeReservationGuest(reservation.guest);
            const guestName = knownGuestName(reservation);
            const emailEditorOpen = activeEmailReservationId === reservationId;
            const showBookerConfirmation =
              !isAssigned &&
              !mode &&
              reservationId === matchingBookerReservationId;

            return (
              <article className="booking-owner-card" key={reservationId}>
                <div className="booking-owner-card-head">
                  <p className="booking-owner-kicker">{t.room}</p>
                  <h2>{roomLabel(reservation, index, t)}</h2>
                  {formatStay(reservation, activeLanguage) && <span>{t.stay}: {formatStay(reservation, activeLanguage)}</span>}
                </div>

                {showBookerConfirmation && (
                  <div className="booking-owner-match-confirmation">
                    <div className="booking-owner-known-guest">
                      <span>{t.knownRoomGuest}</span>
                      <strong>{guestName}</strong>
                    </div>
                    <p>{t.bookerMatchQuestion}</p>
                    <div className="booking-owner-match-actions">
                      <button
                        className="booking-owner-button primary"
                        disabled={current.saving}
                        onClick={() => {
                          chooseBookerRoom(reservationId);
                          submitAssignment(reservation, "booker");
                        }}
                        type="button"
                      >
                        {current.saving ? t.saving : t.bookerIsGuest}
                      </button>
                      <button
                        className="booking-owner-button secondary"
                        disabled={current.saving}
                        onClick={() => {
                          updateAssignment(reservationId, { mode: "guest" });
                          setActiveEmailReservationId(reservationId);
                        }}
                        type="button"
                      >
                        {t.differentGuest}
                      </button>
                    </div>
                  </div>
                )}

                {showGuestFields && (
                  <>
                    {guestName && (
                      <div className="booking-owner-known-guest">
                        <span>{t.knownRoomGuest}</span>
                        <strong>{guestName}</strong>
                      </div>
                    )}
                    <div className="booking-owner-fields">
                      {!guestName && (
                        <>
                          <input
                            aria-label={t.firstName}
                            onChange={(event) => updateAssignment(reservationId, { firstName: event.target.value, mode: "guest" })}
                            placeholder={t.firstName}
                            type="text"
                            value={current.firstName}
                          />
                          <input
                            aria-label={t.lastName}
                            onChange={(event) => updateAssignment(reservationId, { lastName: event.target.value, mode: "guest" })}
                            placeholder={t.lastName}
                            type="text"
                            value={current.lastName}
                          />
                        </>
                      )}
                      {guest.email ? (
                        <div className="booking-owner-known-guest">
                          <span>{t.email}</span>
                          <strong>{guest.email}</strong>
                        </div>
                      ) : emailEditorOpen ? (
                        <input
                          aria-label={t.email}
                          autoComplete="off"
                          autoFocus
                          data-1p-ignore="true"
                          data-lpignore="true"
                          inputMode="email"
                          name={`room-contact-${reservationId}`}
                          onChange={(event) => updateAssignment(reservationId, { email: event.target.value, mode: "guest" })}
                          placeholder={t.email}
                          spellCheck={false}
                          type="text"
                          value={current.email}
                        />
                      ) : (
                        <button
                          className="booking-owner-button secondary"
                          onClick={() => setActiveEmailReservationId(reservationId)}
                          type="button"
                        >
                          {t.enterEmail}
                        </button>
                      )}
                    </div>
                  </>
                )}

                {isAssigned && isDifferentGuest && (
                  <p className="booking-owner-success">
                    {current.emailPending ? t.guestLinkScheduled : t.guestLinkSent}
                  </p>
                )}
                {current.error && <p className="booking-owner-error">{current.error}</p>}
                {reservation.roomAppAvailable === false && isBooker && (
                  <p className="booking-owner-helper">{t.roomAppUnavailable}</p>
                )}

                {reservation.roomAppAvailable === true && isBooker && reservation.ownerJourneyUrl && (
                  <button
                    className="booking-owner-button secondary"
                    onClick={() => navigate(appPathFromUrl(reservation.ownerJourneyUrl, "/home"))}
                    type="button"
                  >
                    {t.openRoomApp}
                  </button>
                )}

                <div className="booking-owner-actions">
                  {showGuestFields && (guest.email || emailEditorOpen) && (
                    <button
                      className="booking-owner-button primary"
                      disabled={current.saving}
                      onClick={() => submitAssignment(reservation, "guest")}
                      type="button"
                    >
                      {current.saving ? t.saving : t.save}
                    </button>
                  )}
                  {showBookerSave && (
                    <button
                      className="booking-owner-button primary"
                      disabled={current.saving}
                      onClick={() => submitAssignment(reservation, "booker")}
                      type="button"
                    >
                      {current.saving ? t.saving : t.save}
                    </button>
                  )}
                  {showStartCheckin && (
                    <button
                      className="booking-owner-button secondary"
                      onClick={() => startOwnerCheckin(reservation)}
                      type="button"
                    >
                      {t.startCheckin}
                    </button>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </section>
    </main>
  );
};

export default BookingOwnerPage;
